import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, styled } from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { YupUtils } from '../../utils/yup.utils'
import ModalForm, { ModalFormProps } from '../common/ModalForm.common'
import { ItemType } from '../../models/props.models'
import { FormItem } from '../../models/props.models'
import { InventoryCheckWaste, ManageInventoryCheckWaste } from '../../models/inventoryChecks.model'
import constants from '../../constants'
import { findCategory } from '../../models/categories.models'
import { WasteCategory } from '../../models/categories.models'

interface ModalFormWasteProps
  extends Omit<ModalFormProps, 'value' | 'setValue' | 'items' | 'title' | 'submitLabel' | 'maxWidth'> {
  inventoryCheckWaste?: InventoryCheckWaste | null
}

const EndAdornmentLabel = styled(Typography)({ fontSize: '0.875rem', fontWeight: 500 })

const ModalFormWaste: React.FC<ModalFormWasteProps> = ({
  inventoryCheckWaste,
  ...formProps
}) => {

  const { t } = useTranslation()
  const [value, setValue] = useState<Partial<ManageInventoryCheckWaste>>({
    name: '',
    categorySelected: undefined,
    totalMass: undefined,
    sortedOnWorkingSite: '',
    supportingDocuments: [],
    supportingDocumentsFile: [],
    declarationOfTruth: false,
    primaryCategory: 0,
    secondaryCategory: 0,
    tertiaryCategory: null,
  })

  const resetValue = useCallback(() => {
    setValue({
      name: '',
      categorySelected: undefined,
      totalMass: undefined,
      sortedOnWorkingSite: '',
      supportingDocuments: [],
      supportingDocumentsFile: [],
      declarationOfTruth: false,
      primaryCategory: 0,
      secondaryCategory: 0,
      tertiaryCategory: null,
    })
  }, [])

  const wasteOptions = useMemo(() => {
    //options are not translated into english
    const flatten = (items: any[]): any[] => {
      let result: any[] = []

      items.forEach((item: any) => {
        if (!item.children) {
          const hierarchy = findCategory('waste', item.id)

          result.push({
            label: item.name,
            value: item.name,
            data: {
              ...hierarchy,
            },
          })
        } else {
          result = result.concat(flatten(item.children))
        }
      })
      return result
    }
    return flatten(WasteCategory as any)
  }, [])

  useEffect(() => {
    if (inventoryCheckWaste) {
      const matchingCategory = wasteOptions.find((option) => {
        return (
          option.data.primaryCategory === inventoryCheckWaste.primaryCategory &&
          option.data.secondaryCategory === inventoryCheckWaste.secondaryCategory &&
          option.data.tertiaryCategory === inventoryCheckWaste.tertiaryCategory
        )
      })
      setValue({
        ...inventoryCheckWaste,
        sortedOnWorkingSite: inventoryCheckWaste.sortedOnWorkingSite ? 'true' : 'false',
        supportingDocumentsFile: [],
        categorySelected: matchingCategory,
      })
    } else {
      resetValue()
    }
  }, [inventoryCheckWaste, resetValue, wasteOptions])

  useEffect(() => {
    if (value.categorySelected) {
      setValue((prev) => ({
        ...prev,
        name: value.categorySelected.value,
        primaryCategory: value.categorySelected.data.primaryCategory,
        secondaryCategory: value.categorySelected.data.secondaryCategory,
        tertiaryCategory: value.categorySelected.data.tertiaryCategory,
      }))
    }
  }, [value.categorySelected])


  const formItems: FormItem[] = useMemo(
    () => [
      {
        type: ItemType.group,
        key: 'wasteDetails',
        props: {
          title: t('inventoryChecks:waste.modalFormWaste.title'),
        },
        items: [
          {
            type: ItemType.asyncSelect,
            key: 'categorySelected',
            required: true,
            grid: { xs: 12 },
            props: {
              label:  t('inventoryChecks:waste.modalFormWaste.categorySelected'),
              placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.categorySelected'),
              options: wasteOptions,
              searchIfEmpty: true,
            },
          },
          {
            type: ItemType.text,
            key: 'additionalInformation',
            grid: { xs: 12, sm: 6 },
            props: {
              label: t('inventoryChecks:waste.modalFormWaste.additionalInformation'),
              placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.additionalInformation'),
              tooltip:
              t('inventoryChecks:waste.modalFormWaste.tooltip.moreDetails'),
            },
          },
          {
            type: ItemType.text,
            key: 'wasteCode',
            grid: { xs: 12, sm: 6 },
            props: {
              label: t('inventoryChecks:waste.modalFormWaste.wasteCode'),
              placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.wasteCode'),

            },
          },
          {
            type: ItemType.number,
            key: 'totalMass',
            required: true,
            grid: { xs: 12, sm: 6 },
            props: {
              label: t('inventoryChecks:waste.modalFormWaste.totalMass'),
              placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.totalMass'),
            },
          },
          {
            type: ItemType.select,
            key: 'sortedOnWorkingSite',
            required: true,
            grid: { xs: 12, sm: 6 },
            props: {
              label: t('inventoryChecks:waste.modalFormWaste.sortedOnWorkingSite'),
              placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.sortedOnWorkingSite'),
              // string in place of boolean cause select has a bug with value : false
              items: [
                { label: t('inventoryChecks:waste.modalFormWaste.sortedOnWorkingSiteOptionA'), value: 'true' },
                { label: t('inventoryChecks:waste.modalFormWaste.sortedOnWorkingSiteOptionB'), value: 'false' },
              ],
            },
          },
        ],
      },
      {
        type: ItemType.group,
        key: 'destination',
        props: {
          title: t('inventoryChecks:waste.modalFormWaste.destination'),
        },
        items: [
          (data: InventoryCheckWaste) => ({
            type: ItemType.typo,
            key: 'percentageByTreatment',
            rules: [
              () => {
                const total =
                  (data?.sortingCenterPercentage ?? 0) +
                  (data?.ecoOrganisationPercentage ?? 0) +
                  (data?.wasteFacilityPercentage ?? 0) +
                  (data?.anotherSitePercentage ?? 0)
                return total > 0 && total !== 100 ? t('errors:sumPercent') : ''
              },
            ],
            props: {
              variant: 'subtitle2',
            },
          }),
          {
            type: ItemType.group,
            key: 'sortingCenter',
            props: {
              description: t('inventoryChecks:waste.modalFormWaste.sortingCenter'),
            },
            items: [
              {
                type: ItemType.number,
                key: 'sortingCenterPercentage',
                rules: [YupUtils.FieldValidationType.percent],
                grid: { xs: 12, sm: 6 },
                props: {
                  label:  t('inventoryChecks:waste.modalFormWaste.percentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
              {
                type: ItemType.text,
                key: 'sortingCenterName',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.locationName'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.sortingCenterName'),
                },
              },
              {
                type: ItemType.address,
                key: 'sortingCenterAddress',
                grid: { xs: 12, sm: 6 },
                rules: [YupUtils.FieldValidationType.fullAddress],
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.address'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.sortingCenterAddress'),
                },
              },
            ],
          },
          {
            type: ItemType.group,
            key: 'ecoOrganisation',
            props: {
              description: t('inventoryChecks:waste.modalFormWaste.ecoOrganisation'),
            },
            items: [
              {
                type: ItemType.number,
                key: 'ecoOrganisationPercentage',
                rules: [YupUtils.FieldValidationType.percent],
                grid: { xs: 12, sm: 6 },
                props: {
                  label:  t('inventoryChecks:waste.modalFormWaste.percentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
              {
                type: ItemType.text,
                key: 'ecoOrganisationName',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.locationName'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.ecoOrganisationName'),
                },
              },
              {
                type: ItemType.address,
                key: 'ecoOrganisationAddress',
                grid: { xs: 12, sm: 6 },
                rules: [YupUtils.FieldValidationType.fullAddress],
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.address'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.ecoOrganisationAddress'),
                },
              },
            ],
          },
          {
            type: ItemType.group,
            key: 'wasteFacility',
            props: {
              description: t('inventoryChecks:waste.modalFormWaste.wasteFacility'),
            },
            items: [
              {
                type: ItemType.number,
                key: 'wasteFacilityPercentage',
                rules: [YupUtils.FieldValidationType.percent],
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.percentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
              {
                type: ItemType.text,
                key: 'wasteFacilityName',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.locationName'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.wasteFacilityName'),
                },
              },
              {
                type: ItemType.address,
                key: 'wasteFacilityAddress',
                grid: { xs: 12, sm: 6 },
                rules: [YupUtils.FieldValidationType.fullAddress],
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.address'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.wasteFacilityAddress'),
                },
              },
            ],
          },
          {
            type: ItemType.group,
            key: 'anotherSite',
            props: {
              description: t('inventoryChecks:waste.modalFormWaste.anotherSite'),
            },
            items: [
              {
                type: ItemType.number,
                key: 'anotherSitePercentage',
                rules: [YupUtils.FieldValidationType.percent],
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.percentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
              {
                type: ItemType.text,
                key: 'anotherSiteName',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.locationName'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.anotherSiteName'),
                },
              },
              {
                type: ItemType.address,
                key: 'anotherSiteAddress',
                grid: { xs: 12, sm: 6 },
                rules: [YupUtils.FieldValidationType.fullAddress],
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.address'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.anotherSiteAddress'),
                },
              },
            ],
          },
        ],
      },
      {
        type: ItemType.group,
        key: 'wasteProcessing',
        props: {
          title: t('inventoryChecks:waste.modalFormWaste.wasteProcessing'),
        },
        items: [
          (data: InventoryCheckWaste) => ({
            type: ItemType.typo,
            key: 'percentageByDestination',
            rules: [
              () => {
                const total =
                  (data?.reusedPercentage ?? 0) +
                  (data?.recyclingPercentage ?? 0) +
                  (data?.quarryBackfillPercentage ?? 0) +
                  (data?.incinerationPercentage ?? 0) +
                  (data?.noValorizedIncinerationPercentage ?? 0) +
                  (data?.landFillPercentage ?? 0)
                return total > 0 && total !== 100 ? t('errors:sumPercent') : ''
              },
            ],
            props: {
              variant: 'subtitle2',
            },
          }),
          {
            type: ItemType.group,
            key: 'wasteValorisation',
            props: {
              description: t('inventoryChecks:waste.modalFormWaste.wasteValorisation'),
            },
            items: [
              {
                type: ItemType.number,
                key: 'reusedPercentage',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.reusedPercentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
              {
                type: ItemType.number,
                key: 'recyclingPercentage',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.recyclingPercentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
              {
                type: ItemType.number,
                key: 'quarryBackfillPercentage',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.quarryBackfillPercentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
            ],
          },
          {
            type: ItemType.group,
            key: 'energyValorization',
            props: {
              description: t('inventoryChecks:waste.modalFormWaste.energyValorization'),
            },
            items: [
              {
                type: ItemType.number,
                key: 'incinerationPercentage',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.incinerationPercentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
            ],
          },
          {
            type: ItemType.group,
            key: 'disposal',
            props: {
              description: t('inventoryChecks:waste.modalFormWaste.disposal'),
            },
            items: [
              {
                type: ItemType.number,
                key: 'noValorizedIncinerationPercentage',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.noValorizedIncinerationPercentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
              {
                type: ItemType.number,
                key: 'landFillPercentage',
                grid: { xs: 12, sm: 6 },
                props: {
                  label: t('inventoryChecks:waste.modalFormWaste.landFillPercentage'),
                  placeholder: t('inventoryChecks:waste.modalFormWaste.placeholders.percentage'),
                  endAdornment: <EndAdornmentLabel>%</EndAdornmentLabel>,
                },
              },
            ],
          },
        ],
      },
      {
        type: ItemType.group,
        key: 'supportingDocumentsGroup',
        props: {
          title: t('inventoryChecks:waste.modalFormWaste.supportingDocumentsGroup'),
        },
        items: [
          {
            type: ItemType.checkbox,
            key: 'declarationOfTruth',
            required: true,
            grid: { xs: 12 },
            props: {
              label:
              t('inventoryChecks:waste.modalFormWaste.declarationOfTruth'),
            },
          },
          {
            type: ItemType.typo,
            key: 'documentsValidation',
            rules: [
              (data: InventoryCheckWaste) => {
                return !(
                  (data.supportingDocumentsFile?.length ?? 0) > 0 ||
                  data.supportingDocuments?.length > 0
                )
                  ? t('inventoryChecks:waste.modalFormWaste.documentsValidation')
                  : ''
              },
            ],
            props: {
              variant: 'subtitle2',
            },
          },
          {
            type: ItemType.documents,
            key: 'supportingDocumentsFile',
            props: {
              type: 'file',
              label: t('inventoryChecks:waste.modalFormWaste.supportingDocumentsFile'),
              documents: value.supportingDocuments || [],
              value: value.supportingDocumentsFile,
              onSelectFiles: (newFiles: File[]) => {
                setValue((state) => ({
                  ...state,
                  supportingDocumentsFile: [...(state.supportingDocumentsFile ?? []), ...newFiles],
                }))
              },
              actionIcon: <DeleteIcon />,
              onActionFile: (index: number) => {
                setValue((state) => ({
                  ...state,
                  supportingDocumentsFile: (state.supportingDocumentsFile ?? []).filter(
                    (_, position) => position !== index,
                  ),
                }))
              },
              onActionDocument: (documentId: string) => {
                setValue((state) => ({
                  ...state,
                  supportingDocuments: (state.supportingDocuments || []).filter(
                    (doc) => doc._id !== documentId,
                  ),
                }))
              },
              maxFileSize: constants.ui.maxSize.pdf,
            },
          },
        ],
      },
    ],
    [value.supportingDocuments, value.supportingDocumentsFile, wasteOptions, t],
  )

  return (
    <ModalForm
    {...formProps}
      value={value}
      setValue={setValue}
      items={formItems}
      title={inventoryCheckWaste ? t('inventoryChecks:waste.action.modifyWaste') : t('inventoryChecks:waste.action.addWaste')}
      submitLabel={inventoryCheckWaste ? t('inventoryChecks:waste.action.modify') : t('inventoryChecks:waste.action.add')}
      maxWidth="md"
    />
  )
}

export default ModalFormWaste
